import type { ReactNode } from 'react';
import type { MonitoringCheck, MonitoringCheckResult } from '@bekebi/contracts/api';
import { CHECK_STATUS_LABELS, formatDateTime } from '../services/format';

/**
 * Monitoring presentation. Everything is read from the API as returned: a check's `lastStatus`,
 * `lastCheckedAt` and `stale` flag, and each result's status, duration, fixed error code/message and
 * details. Nothing is derived here — not health, not staleness, not certificate age.
 */

/** Text shown for a check's current state. A disabled check is "Disabled" — never "Healthy". */
export function checkStateLabel(check: MonitoringCheck): string {
  if (!check.enabled) return 'Disabled';
  if (check.lastStatus === null) return 'No result yet';
  return CHECK_STATUS_LABELS[check.lastStatus];
}

const STATE_STYLE: Record<string, { symbol: string; className: string }> = {
  Passed: { symbol: '✓', className: 'border-success-line bg-success-soft text-success' },
  Warning: { symbol: '!', className: 'border-warning-line bg-warning-soft text-warning' },
  Failed: { symbol: '✕', className: 'border-danger-line bg-danger-soft text-danger' },
  'No result yet': { symbol: '–', className: 'border-line-strong bg-raised text-fg-2' },
  Disabled: { symbol: '⏸', className: 'border-line-strong bg-surface text-muted' },
};

export function CheckStateBadge({ check }: { check: MonitoringCheck }) {
  const label = checkStateLabel(check);
  const style = STATE_STYLE[label]!;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${style.className}`}
      >
        <span aria-hidden="true">{style.symbol}</span>
        {label}
      </span>
      {check.enabled && check.lastStatus !== null && check.stale ? <StaleBadge /> : null}
    </span>
  );
}

/** The API marked the latest result as out of date: it is not current evidence. */
export function StaleBadge() {
  return (
    <span className="inline-flex items-center rounded-full border border-warning-line bg-surface px-2.5 py-0.5 text-xs font-semibold text-warning">
      Stale
    </span>
  );
}

/**
 * When a new result can be expected. Results only arrive while the monitoring worker runs; the
 * portal has no API that says whether it does, so this never promises a time.
 */
export const NEXT_RESULT_NOTE =
  'A new result appears when the monitoring worker next runs the check.';

export const STALE_EXPLANATION =
  'Stale: no result arrived within the expected time, so the latest result is not treated as current.';

/** Where a check connects, from its stored target (no credentials exist in targets). */
export function targetSummary(check: MonitoringCheck): string {
  const target = check.target as Record<string, unknown>;
  if (check.kind === 'website_https') return 'The Website’s public address';
  if (check.kind === 'nextcloud_status') return `${String(target.baseUrl ?? '')}/status.php`;
  if (check.kind === 'mail_dns') {
    return `${String(target.domain ?? '')}: MX, SPF, DMARC, DKIM (selector ${String(target.dkimSelector ?? '')}), reverse DNS`;
  }
  const host = String(target.host ?? '');
  const port = String(target.port ?? '');
  if (check.kind === 'smtp_tls') {
    const security = target.security === 'starttls' ? 'STARTTLS' : 'implicit TLS';
    return `${host}:${port} (${security})`;
  }
  return `${host}:${port} (implicit TLS)`;
}

export function formatInterval(seconds: number): string {
  if (seconds % 3600 === 0) return `every ${seconds / 3600} h`;
  if (seconds % 60 === 0) return `every ${seconds / 60} min`;
  return `every ${seconds} s`;
}

const RESULT_STATUS_LABELS: Record<MonitoringCheckResult['status'], string> = {
  ok: 'Passed',
  warning: 'Warning',
  failed: 'Failed',
};

export function ResultStatus({ status }: { status: MonitoringCheckResult['status'] }) {
  const label = RESULT_STATUS_LABELS[status];
  const style = STATE_STYLE[label]!;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${style.className}`}
    >
      <span aria-hidden="true">{style.symbol}</span>
      {label}
    </span>
  );
}

type CheckErrorCode = NonNullable<MonitoringCheckResult['errorCode']>;

/**
 * What to do about each failure or warning — fixed guidance per error code, so a result is
 * actionable without exposing anything beyond what the probe recorded.
 */
export const CHECK_ERROR_ACTIONS: Record<CheckErrorCode, string> = {
  timeout:
    'Check that the service is running and reachable from the portal host; a slow or overloaded server can also cause this.',
  dns_error: 'Check the host name in the check, and that its DNS record exists.',
  connection_refused:
    'Nothing accepted the connection on that port: check that the service is running and the port is open in the firewall.',
  connection_error:
    'The connection dropped: check the service and any firewall or proxy between the portal host and the server.',
  tls_error:
    'The certificate could not be verified for this host name: check that a valid certificate for this name is installed.',
  tls_certificate_expired: 'Renew the TLS certificate now; clients will refuse the connection.',
  tls_certificate_expiring: 'Renew the TLS certificate before the expiry date shown.',
  http_status: 'Open the address and check the server and application logs for the error status.',
  too_many_redirects: 'Check the web server or proxy redirect rules for a loop.',
  invalid_redirect: 'Make every redirect point to an HTTPS address.',
  unexpected_response:
    'The service answered, but not as expected: check that this address serves the right application.',
  unexpected_greeting: 'Check that the right mail service listens on this host and port.',
  starttls_unavailable: 'Enable STARTTLS on this port in the mail server configuration.',
  maintenance_mode:
    'Nextcloud is in maintenance mode: finish the maintenance and turn it off (occ maintenance:mode --off).',
  needs_db_upgrade: 'Nextcloud is waiting for a database upgrade: run the upgrade (occ upgrade).',
  not_installed:
    'Nextcloud reports it is not installed: check the installation and its configuration.',
  target_not_configured: 'Edit the check and enter a valid target.',
  target_not_allowed:
    'The target is outside MONITORING_ALLOWED_DOMAINS: correct the target, or have the owner extend the allow-list.',
  dns_record_missing:
    'Add the missing record(s) listed under "Missing records" at the domain’s DNS provider.',
  reverse_dns_mismatch:
    'In the hosting provider’s control panel, set the reverse DNS (PTR) of the mail server’s address to the mail host name shown under "Mail host".',
};

// --- Result details: only fields the probes actually record, each type-checked before display ---

const str = (v: unknown) => (typeof v === 'string' ? v : null);
const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const bool = (v: unknown) => (typeof v === 'boolean' ? v : null);
const yesNo = (v: boolean) => (v ? 'Yes' : 'No');

interface Certificate {
  host: string;
  validTo: string;
}
function certificates(v: unknown): Certificate[] {
  if (!Array.isArray(v)) return [];
  return v.filter(
    (c): c is Certificate =>
      typeof c === 'object' &&
      c !== null &&
      typeof (c as Certificate).host === 'string' &&
      typeof (c as Certificate).validTo === 'string',
  );
}
function strings(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : [];
}
/** { address: [names] } as recorded by the mail DNS check. */
function reverseDns(v: unknown): [string, string[]][] {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return [];
  return Object.entries(v as Record<string, unknown>).map(([address, names]) => [
    address,
    strings(names),
  ]);
}

/** The details of one result, as recorded by the worker. Unknown fields are not shown. */
export function ResultDetails({ result }: { result: MonitoringCheckResult }) {
  const d = result.details;
  const rows: [string, ReactNode][] = [];
  const add = (label: string, value: ReactNode | null) => {
    if (value !== null && value !== '') rows.push([label, value]);
  };

  add('Checked at', formatDateTime(result.checkedAt));
  add('Check duration', result.durationMs !== null ? `${result.durationMs} ms` : null);
  if (result.errorCode) {
    add(
      result.status === 'warning' ? 'Warning' : 'Error',
      <>
        {result.errorMessage ?? ''} <code className="text-xs text-muted">{result.errorCode}</code>
      </>,
    );
    add('What to do', CHECK_ERROR_ACTIONS[result.errorCode]);
  }
  add('Address', str(d.url));
  add('Final address', str(d.finalUrl));
  const statusCode = num(d.statusCode);
  add('HTTP status', statusCode !== null ? String(statusCode) : null);
  const responseTime = num(d.responseTimeMs);
  add('Response time', responseTime !== null ? `${responseTime} ms` : null);
  const redirects = strings(d.redirects);
  if (Array.isArray(d.redirects)) {
    add(
      'Redirects',
      redirects.length === 0 ? (
        'None'
      ) : (
        <ol className="list-decimal pl-5">
          {redirects.map((r, i) => (
            <li key={i} className="break-all font-mono text-xs">
              {r}
            </li>
          ))}
        </ol>
      ),
    );
  }
  const installed = bool(d.installed);
  add('Installed', installed !== null ? yesNo(installed) : null);
  const maintenance = bool(d.maintenance);
  add('Maintenance mode', maintenance !== null ? yesNo(maintenance) : null);
  const upgrade = bool(d.needsDbUpgrade);
  add('Database upgrade pending', upgrade !== null ? yesNo(upgrade) : null);
  add('Application version', str(d.version));
  const host = str(d.host);
  const port = num(d.port);
  add('Host', host !== null ? `${host}${port !== null ? `:${port}` : ''}` : null);
  add('Connection security', str(d.security));
  add('Greeting', str(d.greetingCode) ?? str(d.greeting));
  const starttls = bool(d.starttls);
  add('STARTTLS used', starttls !== null ? yesNo(starttls) : null);
  add('TLS protocol', str(d.tlsProtocol));
  // Mail DNS check (mail_dns)
  add('Domain', str(d.domain));
  const mx = strings(d.mx);
  if (Array.isArray(d.mx)) add('MX records', mx.length === 0 ? 'None' : mx.join(', '));
  const spf = bool(d.spf);
  add('SPF record', spf !== null ? (spf ? 'Present' : 'Missing') : null);
  if ('dmarcPolicy' in d) add('DMARC policy', str(d.dmarcPolicy) ?? 'No DMARC record');
  const dkim = bool(d.dkim);
  const selector = str(d.dkimSelector);
  add(
    `DKIM record${selector !== null ? ` (selector ${selector})` : ''}`,
    dkim !== null ? (dkim ? 'Present' : 'Missing') : null,
  );
  add('Mail host', str(d.mxHost));
  const mxAddresses = strings(d.mxAddresses);
  add('Mail host addresses', mxAddresses.length > 0 ? mxAddresses.join(', ') : null);
  const reverse = reverseDns(d.reverseDns);
  if (reverse.length > 0) {
    add(
      'Reverse DNS (PTR)',
      <ul>
        {reverse.map(([address, names]) => (
          <li key={address}>
            <span className="font-mono text-xs">{address}</span>:{' '}
            {names.length > 0 ? names.join(', ') : 'none'}
          </li>
        ))}
      </ul>,
    );
  }
  const missing = strings(d.missing);
  add('Missing records', missing.length > 0 ? missing.join(', ') : null);

  const certs = certificates(d.certificates);
  if (certs.length > 0) {
    add(
      'TLS certificate expiry',
      <ul>
        {certs.map((c, i) => (
          <li key={i}>
            <span className="font-mono text-xs">{c.host}</span>: {formatDateTime(c.validTo)}
          </li>
        ))}
      </ul>,
    );
  }

  return (
    <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[max-content_1fr]">
      {rows.map(([term, value]) => (
        <div key={term} className="contents">
          <dt className="font-medium text-muted">{term}</dt>
          <dd className="break-words text-fg">{value}</dd>
        </div>
      ))}
    </dl>
  );
}
